import { AlertTriangle, CheckCircle2, LoaderCircle, Send, Users } from 'lucide-react'
import { useState } from 'react'
import { procurementScope } from '../../shared/shutters'
import { Link } from 'react-router-dom'
import type { PricingItem } from '../features/pricingItems/types'
import { useApi } from '../hooks/useApi'
import { api } from '../lib/apiClient'
import type { AppRecord } from '../types'
import { Modal } from './Modal'
import { ErrorState, LoadingState } from './States'

export type SendRfqResult = {
  created: Array<{
    supplierId: string
    supplierName: string
    quoteId: string
    quoteCode: string
    supplierQuoteItemCount: number
    reusedDraft: boolean
  }>
  failed: Array<{ supplierId: string; supplierName: string; code: string; message: string; quoteId?: string; quoteCode?: string; recoverable?: boolean }>
}

function SupplierSelector({ opportunityId, onClose, onComplete, onBusyChange }: {
  opportunityId: string
  onClose: () => void
  onComplete: (result: SendRfqResult) => void
  onBusyChange: (busy: boolean) => void
}) {
  const [selected, setSelected] = useState<string[]>([])
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [sending, setSending] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const suppliersRequest = useApi(() => api.list<AppRecord>('suppliers', '?rfqEligible=true'), [])
  const pricingRequest = useApi(() => api.related<PricingItem>('opportunities', opportunityId, 'pricing-items'), [opportunityId])
  const pricingItems = (pricingRequest.data ?? []).filter((item) => item.status !== 'discarded')
  const scopeIds = selectedItems
  const invalidPricing = pricingItems.some((item) => scopeIds.includes(item.id) && !item.configurationStatus.valid)
  const toggle = (supplierId: string) => {
    setSelected((current) => current.includes(supplierId) ? current.filter((id) => id !== supplierId) : [...current, supplierId])
  }
  const toggleAllSuppliers = () => {
    const supplierIds = suppliers.map((supplier) => supplier.id)
    setSelected((current) => supplierIds.length > 0 && supplierIds.every((id) => current.includes(id)) ? [] : supplierIds)
  }
  const confirm = async () => {
    if (!selected.length || !scopeIds.length || invalidPricing || sending) return
    setSending(true)
    onBusyChange(true)
    setActionError(null)
    try {
      const result = await api.createRelated<SendRfqResult>('opportunities', opportunityId, 'supplier-rfqs', { supplierIds: selected, pricingItemIds: scopeIds })
      onBusyChange(false)
      onComplete(result)
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : 'The supplier requests could not be prepared.')
      setSending(false)
      onBusyChange(false)
    }
  }
  const suppliers = suppliersRequest.data ?? []
  const loading = suppliersRequest.loading || pricingRequest.loading
  return (
    <>
      <div className="max-h-[62vh] overflow-y-auto px-5 py-5 sm:px-6">
        {actionError && <div className="mb-4 flex items-start gap-2.5 rounded-lg bg-rose-50 px-3.5 py-3 text-sm text-rose-700"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{actionError}</div>}
        {loading ? <LoadingState label="Loading suppliers and Pricing Items…" /> : suppliersRequest.error || pricingRequest.error ? (
          <ErrorState message={suppliersRequest.error || pricingRequest.error || 'Could not load RFQ prerequisites.'} onRetry={async () => { await Promise.all([suppliersRequest.reload(), pricingRequest.reload()]) }} />
        ) : (
          <>
            <div className={`mb-5 rounded-xl border px-4 py-3 ${pricingItems.length && !invalidPricing ? 'border-pine-200 bg-pine-50 text-pine-900' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
              <div className="font-bold">Available pricing configurations: {pricingItems.length}</div>
              <p className="mt-1 text-sm">Select any combination of supported product types. Every selected supplier receives the same ordered item set.</p>
              <fieldset disabled={sending} className="mt-3 space-y-3">{['fenestration', 'shutters', 'mosquito_nets', 'other'].map((scope) => {
                const scopeItems = pricingItems.filter((item) => (procurementScope(item.elementType) ?? 'other') === scope)
                const scopeDisabled = scope === 'other'
                const allScopeSelected = scopeItems.length > 0 && scopeItems.every((item) => scopeIds.includes(item.id))
                const scopeItemIds = new Set(scopeItems.map((item) => item.id))
                const toggleScope = () => setSelectedItems((current) => allScopeSelected
                  ? current.filter((id) => !scopeItemIds.has(id))
                  : [...current, ...scopeItems.map((item) => item.id).filter((id) => !current.includes(id))])
                return <div key={scope}><div className="flex flex-wrap items-center justify-between gap-2"><h4 className="font-semibold">{scope === 'fenestration' ? 'Windows / Doors' : scope === 'shutters' ? 'Shutters' : scope === 'mosquito_nets' ? 'Mosquito Nets' : 'Unsupported / review required'}</h4>{scope !== 'other' && scopeItems.length > 0 && <label className={`flex cursor-pointer items-center gap-2 text-xs font-semibold ${scopeDisabled ? 'text-slate-400' : 'text-pine-700'}`}><input type="checkbox" disabled={scopeDisabled} checked={allScopeSelected} onChange={toggleScope} />Select all</label>}</div>{scopeItems.map((item) => <label key={item.id} className={`flex gap-2 py-1 ${scopeDisabled ? 'text-slate-400' : ''}`}><input type="checkbox" disabled={scopeDisabled} checked={scopeIds.includes(item.id)} onChange={() => setSelectedItems(scopeIds.includes(item.id) ? scopeIds.filter((id) => id !== item.id) : [...scopeIds, item.id])} />{item.displayLabel} — {item.summary.dimensions}</label>)}</div>
              })}</fieldset>
              {pricingItems.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">{pricingItems.map((item) => <span key={item.id} className="rounded-md border border-current/15 bg-white/60 px-2 py-1 font-mono text-xs font-bold">{item.displayLabel}</span>)}</div>}
              {!pricingItems.length && <p className="mt-2 text-sm font-semibold">Prepare pricing before creating supplier RFQs.</p>}
              {invalidPricing && <p className="mt-2 text-sm font-semibold">Review invalid Pricing Items before continuing.</p>}
            </div>
            {suppliers.length ? (
              <fieldset className="space-y-2" disabled={sending}>
                <legend className="sr-only">Eligible suppliers</legend>
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-700"><input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-pine-700 focus:ring-pine-500" checked={suppliers.every((supplier) => selected.includes(supplier.id))} onChange={toggleAllSuppliers} />Select all suppliers</label>
                {suppliers.map((supplier) => {
                  const checked = selected.includes(supplier.id)
                  return (
                    <label key={supplier.id} className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3.5 transition ${checked ? 'border-pine-400 bg-pine-50 ring-1 ring-pine-200' : 'border-slate-200 bg-white hover:border-pine-200 hover:bg-pine-50/40'}`}>
                      <input type="checkbox" className="mt-1 h-4 w-4 rounded border-slate-300 text-pine-700 focus:ring-pine-500" checked={checked} onChange={() => toggle(supplier.id)} />
                      <span className="min-w-0 flex-1">
                        <span className="block font-bold text-slate-900">{String(supplier.name || 'Unnamed supplier')}</span>
                        <span className="mt-1 block text-xs font-semibold uppercase tracking-wide text-pine-700">{String(supplier.supplierType || '')}</span>
                        <span className="mt-1 block text-sm text-slate-500">{[supplier.contactPerson, supplier.email].filter(Boolean).join(' · ') || 'No contact details recorded'}</span>
                      </span>
                    </label>
                  )
                })}
              </fieldset>
            ) : <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">No active Manufacturer or Reseller suppliers are available.</div>}
          </>
        )}
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/70 px-5 py-4 sm:px-6">
        <span className="text-sm font-semibold text-slate-500">{selected.length} supplier{selected.length === 1 ? '' : 's'} selected</span>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={sending}>Cancel</button>
          <button type="button" className="btn-primary" onClick={() => void confirm()} disabled={!selected.length || !scopeIds.length || invalidPricing || sending || loading}>
            {sending ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? 'Preparing…' : 'Create RFQs'}
          </button>
        </div>
      </div>
    </>
  )
}

export function SendRfqButton({ opportunityId, onComplete }: { opportunityId: string; onComplete?: () => void | Promise<void> }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<SendRfqResult | null>(null)
  const complete = (value: SendRfqResult) => {
    setResult(value)
    setOpen(false)
    void onComplete?.()
  }
  return (
    <div className="flex flex-col items-end gap-1.5">
      <button className="btn-primary" disabled={busy} onClick={() => { setResult(null); setOpen(true) }}><Users className="h-4 w-4" />Create supplier RFQ</button>
      {result && (
        <div className={`max-w-sm rounded-lg border px-3 py-2 text-xs shadow-sm ${result.failed.length ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-pine-200 bg-pine-50 text-pine-900'}`}>
          <div className="flex items-start gap-2">
            {result.failed.length ? <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
            <div>
              <div className="font-bold">{result.created.length ? `Prepared ${result.created.length} supplier request${result.created.length === 1 ? '' : 's'}.` : 'RFQ preparation incomplete.'}</div>
              <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1">{result.created.map((created) => <Link key={created.quoteId} to={`/quotes/${created.quoteId}`} className="font-bold underline hover:no-underline">{created.quoteCode}{created.reusedDraft ? ' (updated draft)' : ''}</Link>)}</div>
              {result.failed.map((failure) => <div key={failure.supplierId} className="mt-1">{failure.supplierName}: {failure.recoverable && failure.quoteId ? <><span className="font-bold">Draft saved; RFQ preparation incomplete.</span>{' '}<Link to={`/quotes/${failure.quoteId}`} className="font-bold underline">{failure.quoteCode || 'Open saved draft'}</Link>{' '}Reload before retrying Create RFQs to complete this draft.{' '}</> : null}{failure.message}</div>)}
            </div>
          </div>
        </div>
      )}
      <Modal open={open} onClose={() => { if (!busy) setOpen(false) }} title="Create supplier RFQs" description="Select suppliers. Existing drafts are safely synchronised; sent requests are preserved." size="lg">
        <SupplierSelector opportunityId={opportunityId} onClose={() => setOpen(false)} onComplete={complete} onBusyChange={setBusy} />
      </Modal>
    </div>
  )
}
